import EXERCISE_DB from './data/exerciseDatabase';
import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import {
  collection, addDoc, getDocs, query, orderBy, limit,
  Timestamp, deleteDoc, doc
} from "firebase/firestore";
import { btnStyle, backBtn, inputStyle, labelStyle, tabBtn } from "./styles";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import SwipeToDelete from "./components/SwipeToDelete";



const styleTag = document.createElement("style");
styleTag.innerHTML = `
  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .fade-slide { animation: fadeSlideIn 0.35s ease both; }
  .card-hover { transition: transform 0.18s ease, box-shadow 0.18s ease; cursor: pointer; }
  .card-hover:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.35); }
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; }
`;
if (!document.getElementById("mgl-styles")) {
  styleTag.id = "mgl-styles";
  document.head.appendChild(styleTag);
}

// ─── EXERCISE FOOTER (Rest Timer per exercise) ───────────────────────────────
function ExerciseFooter({ exIndex, addSet }) {
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerTotal, setTimerTotal] = useState(0);
  const [running, setRunning] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    if (!running) return;
    if (timerSeconds <= 0) { setRunning(false); return; }
    const interval = setInterval(() => setTimerSeconds((s) => s - 1), 1000);
    return () => clearInterval(interval);
  }, [running, timerSeconds]);

  const startRest = (seconds) => {
    setTimerTotal(seconds);
    setTimerSeconds(seconds);
    setRunning(true);
    setShowPicker(false);
  };

  const stopTimer = () => { setRunning(false); setTimerSeconds(0); setTimerTotal(0); };
  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const pct = timerTotal > 0 ? (timerSeconds / timerTotal) * 100 : 0;

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => addSet(exIndex)} style={{
          flex: 1, background: "#374151", color: "#F9FAFB", border: "none",
          borderRadius: 10, padding: "9px 0", cursor: "pointer", fontSize: 13, fontWeight: "700"
        }}>+ Add Set</button>
        <button onClick={() => { if (running) { stopTimer(); } else { setShowPicker(!showPicker); } }} style={{
          flex: 1, background: running ? "#059669" : "#4F46E5", color: "white", border: "none",
          borderRadius: 10, padding: "9px 0", cursor: "pointer", fontSize: 13, fontWeight: "700", transition: "background 0.2s"
        }}>
          {running ? `⏱ ${formatTime(timerSeconds)}` : "⏱ Rest Timer"}
        </button>
      </div>
      {showPicker && !running && (
        <div className="fade-slide" style={{ background: "#111827", border: "1px solid #374151", borderRadius: 12, padding: 12, marginTop: 8 }}>
          <p style={{ color: "#9CA3AF", fontSize: 12, fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px", margin: "0 0 10px 0" }}>Select Rest Duration</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6 }}>
            {[45, 60, 90, 120, 150, 180, 240, 300].map((s) => (
              <button key={s} onClick={() => startRest(s)} style={{
                background: "#1F2937", color: "#F9FAFB", border: "1px solid #374151",
                borderRadius: 8, padding: "8px 4px", cursor: "pointer", fontSize: 13, fontWeight: "700"
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#374151"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#1F2937"}>
                {s < 60 ? `${s}s` : `${Math.floor(s/60)}${s%60 !== 0 ? `:${s%60}` : ""}min`}
              </button>
            ))}
          </div>
        </div>
      )}
      {running && (
        <div style={{ marginTop: 8 }}>
          <div style={{ background: "#374151", borderRadius: 6, height: 5 }}>
            <div style={{ background: timerSeconds <= 10 ? "#EF4444" : "#4F46E5", height: 5, borderRadius: 6, width: `${pct}%`, transition: "width 1s linear" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
            <span style={{ color: timerSeconds <= 10 ? "#EF4444" : "#9CA3AF", fontSize: 13, fontWeight: "700" }}>{formatTime(timerSeconds)} remaining</span>
            <button onClick={stopTimer} style={{ background: "none", border: "none", color: "#6B7280", cursor: "pointer", fontSize: 12 }}>✕ Stop</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HOME SCREEN ─────────────────────────────────────────────────────────────
export function HomeScreen({ userId, plans, activePlanIndex, lastTrained, onSelectPlan, onNewPlan, onEditPlan, onDeletePlan, onStartDay, onCustomWorkout, onRun, onHistory, onBodyWeight }) {
  const [planOpen, setPlanOpen] = useState(true);
  const [stats, setStats] = useState({ week: 0, month: 0, total: 0 });
  const activePlan = plans[activePlanIndex] || null;
  const hasPlan = activePlan && activePlan.days && activePlan.days.length > 0;

  useEffect(() => {
    if (!userId) return;
    const fetchStats = async () => {
      const snap = await getDocs(query(collection(db, "users", userId, "workouts"), orderBy("date", "desc")));
      const dates = snap.docs.map(d => d.data().date?.toDate()).filter(Boolean);
      const now = new Date();

      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);

      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      setStats({
        week: dates.filter(d => d >= startOfWeek).length,
        month: dates.filter(d => d >= startOfMonth).length,
        total: dates.length,
      });
    };
    fetchStats();
  }, [userId]);

  return (
    <div className="fade-slide">
      {userId && (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20, marginTop: 8 }}>
    {[
      { label: "This Week", value: stats.week, color: "#4F46E5" },
      { label: "This Month", value: stats.month, color: "#F97316" },
      { label: "All Time", value: stats.total, color: "#059669" },
    ].map(s => (
      <div key={s.label} style={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 12, padding: "12px 8px", textAlign: "center" }}>
        <div style={{ fontSize: 22, fontWeight: "800", color: s.color }}>{s.value}</div>
        <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "700" }}>{s.label}</div>
      </div>
    ))}
  </div>
)}
      {plans.length === 0 && (
        <div style={{ background: "linear-gradient(135deg, #1F2937, #111827)", border: "1px solid #374151", borderRadius: 16, padding: 20, marginBottom: 20, marginTop: 8 }}>
          <p style={{ margin: "0 0 8px 0", fontWeight: "800", color: "#F97316", fontSize: 18 }}>👋 Welcome to MyGainLog!</p>
          <p style={{ margin: "0 0 16px 0", color: "#9CA3AF", fontSize: 14, lineHeight: 1.5 }}>Start by creating your first workout plan.</p>
          <button onClick={onNewPlan} style={btnStyle("#F97316")}>📋 Create Workout Plan</button>
        </div>
      )}
      {plans.length > 0 && (
        <div style={{ marginBottom: 8, marginTop: 8 }}>
          {plans.length > 1 && (
            <div style={{ display: "flex", gap: 8, marginBottom: 14, overflowX: "auto", paddingBottom: 4 }}>
              {plans.map((p, i) => (
                <button key={i} onClick={() => onSelectPlan(i)} style={{
                  background: activePlanIndex === i ? "#4F46E5" : "#1F2937",
                  color: activePlanIndex === i ? "white" : "#9CA3AF",
                  border: "1px solid #374151", borderRadius: 20,
                  padding: "6px 16px", cursor: "pointer", fontSize: 13, fontWeight: "700", whiteSpace: "nowrap"
                }}>{p.name || `Plan ${i + 1}`}</button>
              ))}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: planOpen ? 14 : 6 }}>
            <button onClick={() => setPlanOpen(!planOpen)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: 0 }}>
              <span style={{ fontWeight: "800", fontSize: 18, color: "#F9FAFB" }}>{activePlan?.name || "My Training Plan"}</span>
              <span style={{ color: "#6B7280", fontSize: 18, transition: "transform 0.25s", transform: planOpen ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block" }}>›</span>
            </button>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onEditPlan} style={{ background: "none", border: "1px solid #374151", color: "#9CA3AF", cursor: "pointer", fontSize: 13, borderRadius: 8, padding: "4px 12px" }}>Edit</button>
              {plans.length > 1 && (
                <button onClick={() => { if (window.confirm(`Delete "${activePlan?.name}"?`)) onDeletePlan(activePlanIndex); }}
                  style={{ background: "none", border: "1px solid #374151", color: "#EF4444", cursor: "pointer", fontSize: 13, borderRadius: 8, padding: "4px 10px" }}>🗑</button>
              )}
            </div>
          </div>
          {planOpen && hasPlan && activePlan.days.map((day, i) => {
            const last = lastTrained?.[day.name];
            return (
              <button key={i} onClick={() => onStartDay(day)} className="card-hover fade-slide"
                style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", width: "100%", background: "#1F2937", border: "1px solid #374151", borderRadius: 14, padding: "14px 18px", marginBottom: 10, textAlign: "left", color: "#F9FAFB", animationDelay: `${i * 0.06}s` }}>
                <div>
                  <span style={{ fontWeight: "800", fontSize: 16, display: "block" }}>{day.name}</span>
                  <span style={{ fontSize: 12, color: last ? "#6B7280" : "#4B5563", marginTop: 2, display: "block" }}>
                    {last ? <>Last: <span style={{ color: "#4F46E5", fontWeight: "600" }}>{last}</span></> : "Not trained yet"}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ background: day.location === "Home" ? "#7C3AED" : "#F97316", color: "white", fontSize: 12, fontWeight: "700", padding: "3px 10px", borderRadius: 20 }}>{day.location}</span>
                  <span style={{ color: "#6B7280", fontSize: 20 }}>›</span>
                </div>
              </button>
            );
          })}
          {planOpen && !hasPlan && <p style={{ color: "#6B7280", fontSize: 14, marginBottom: 16 }}>No days yet — press Edit to add days.</p>}
        </div>
      )}
      <div style={{ marginTop: plans.length > 0 ? 24 : 0 }}>
        <div style={{ borderTop: plans.length > 0 ? "1px solid #1F2937" : "none", paddingTop: plans.length > 0 ? 20 : 0 }}>
          <p style={{ color: "#6B7280", fontSize: 12, fontWeight: "700", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 12 }}>Quick Actions</p>
          <button onClick={onNewPlan} style={btnStyle("#F97316")}>📋 New Training Plan</button>
          <button onClick={onCustomWorkout} style={btnStyle("#4F46E5")}>💪 Start Custom Workout</button>
          <button onClick={onRun} style={btnStyle("#059669")}>🏃 Log a Run</button>
          <button onClick={onHistory} style={btnStyle("#374151")}>📋 View History</button>
          <button onClick={onBodyWeight} style={btnStyle("#0EA5E9")}>⚖️ Body Weight</button>
        </div>
      </div>
    </div>
  );
}

// ─── CREATE PLAN SCREEN ───────────────────────────────────────────────────────
export function CreatePlanScreen({ existingPlan, onSave, onBack }) {
  const [planName, setPlanName] = useState(existingPlan?.name || "");
  const [days, setDays] = useState(existingPlan?.days || []);
  const [newDayName, setNewDayName] = useState("");
  const [editingDay, setEditingDay] = useState(null);

  const addDay = () => {
    if (!newDayName.trim()) return;
    setDays([...days, { name: newDayName.trim(), location: "Gym", exercises: [] }]);
    setNewDayName("");
  };
  const removeDay = (i) => setDays(days.filter((_, idx) => idx !== i));
  const updateDay = (i, updated) => { const d = [...days]; d[i] = updated; setDays(d); };

  if (editingDay !== null) {
    return (
      <EditDayScreen
        day={days[editingDay]}
        onSave={(updated) => { updateDay(editingDay, updated); setEditingDay(null); }}
        onBack={() => setEditingDay(null)}
      />
    );
  }

  return (
    <div className="fade-slide">
      <button onClick={onBack} style={backBtn}>← Back</button>
      <h2>📋 Workout Plan</h2>
      <label style={labelStyle}>Plan name</label>
      <input value={planName} onChange={(e) => setPlanName(e.target.value)}
        placeholder="e.g. PPL, Upper/Lower, Arnold Split"
        style={{ ...inputStyle, marginBottom: 20 }} />
      <label style={labelStyle}>Days</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 
        }}>
        <input value={newDayName} onChange={(e) => setNewDayName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addDay()}
          placeholder="Day name (e.g. Push, Pull, Legs)" style={inputStyle} />
        <button onClick={addDay} style={btnStyle("#4F46E5", true)}>Add</button>
      </div>
      {days.length === 0 && <p style={{ color: "#9CA3AF" }}>No days yet — add your first day above.</p>}
      {days.map((day, i) => (
        <div key={i} style={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 14, padding: 16, marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <span style={{ fontWeight: "800", fontSize: 16, color: "#F9FAFB" }}>{day.name}</span>
              <span style={{ background: day.location === "Home" ? "#7C3AED" : "#F97316", color: "white", fontSize: 12, fontWeight: "700", padding: "2px 8px", borderRadius: 20, marginLeft: 10 }}>{day.location}</span>
              <div style={{ fontSize: 13, color: "#6B7280", marginTop: 4 }}>
                {day.exercises?.length > 0 ? `${day.exercises.length} exercise${day.exercises.length !== 1 ? "s" : ""}` : "No exercises yet"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setEditingDay(i)} style={{ background: "#374151", color: "#F9FAFB", border: "none", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: "600" }}>Edit</button>
              <button onClick={() => removeDay(i)} style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
          </div>
        </div>
      ))}
      {days.length > 0 && <button onClick={() => onSave({ name: planName || "My Training Plan", days })} style={{ ...btnStyle("#059669"), marginTop: 8 }}>💾 Save Plan</button>}
    </div>
  );
}

// ─── EDIT DAY SCREEN ─────────────────────────────────────────────────────────
export function EditDayScreen({ day, onSave, onBack }) {
  const [name, setName] = useState(day.name);
  const [location, setLocation] = useState(day.location || "Gym");
  const [exercises, setExercises] = useState(day.exercises);
  const [exName, setExName] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const handleExNameChange = (val) => {
    setExName(val);
    if (val.trim().length < 1) { setSuggestions([]); return; }

    const input = val.toLowerCase();

    const dbMatches = EXERCISE_DB.filter(e =>
      e.name.toLowerCase().includes(input)
    );

    const matchedMuscles = new Set(dbMatches.flatMap(e => e.muscles));
    const related = EXERCISE_DB.filter(e =>
      !dbMatches.includes(e) &&
      e.muscles.some(m => matchedMuscles.has(m))
    );

    const dbNames = dbMatches.map(e => e.name);
    const relatedNames = related.map(e => e.name);
    const combined = [...new Set([...dbNames, ...relatedNames])];
    setSuggestions(combined.slice(0, 7));
  };

  const addExercise = (nameOverride) => {
    const n = (nameOverride || exName).trim();
    if (!n) return;
    setExercises([...exercises, { name: n }]);
    setExName("");
    setSuggestions([]);
  };

  const removeExercise = (i) => setExercises(exercises.filter((_, idx) => idx !== i));
  return (
    <div className="fade-slide">
      <button onClick={onBack} style={backBtn}>← Back</button>
      <h2>Edit: {day.name}</h2>
      <label style={labelStyle}>DAY NAME</label>
      <input value={name} onChange={(e) => setName(e.target.value)} style={{ ...inputStyle, marginBottom: 16 }} />
      <label style={labelStyle}>LOCATION</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {["Gym", "Home"].map((loc) => (
          <button key={loc} onClick={() => setLocation(loc)}
            style={{ ...tabBtn, background: location === loc ? (loc === "Gym" ? "#F97316" : "#7C3AED") : "#1F2937", color: location === loc ? "white" : "#9CA3AF", border: "1px solid #374151" }}>
            {loc === "Gym" ? "🏋️" : "🏠"} {loc}
          </button>
        ))}
      </div>
      <label style={labelStyle}>EXERCISES</label>
      <div style={{ position: "relative", marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={exName}
            onChange={(e) => handleExNameChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addExercise()}
            placeholder="Exercise name (e.g. Bench Press)"
            style={inputStyle}
          />
          <button onClick={() => addExercise()} style={btnStyle("#4F46E5", true)}>Add</button>
        </div>
        {suggestions.length > 0 && (
          <div style={{ position: "absolute", top: "100%", left: 0, right: 48, background: "#1F2937", border: "1px solid #374151", borderRadius: 10, zIndex: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", overflow: "hidden" }}>
            {suggestions.map((s, i) => (
              <div key={i} onClick={() => addExercise(s)}
                style={{ padding: "11px 14px", cursor: "pointer", color: "#F9FAFB", fontSize: 14, fontWeight: "600", borderBottom: i < suggestions.length - 1 ? "1px solid #374151" : "none" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#374151"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>{s}</div>
            ))}
          </div>
        )}
      </div>
      {exercises.length === 0 && <p style={{ color: "#9CA3AF", fontSize: 14 }}>No exercises yet.</p>}
      {exercises.map((ex, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#1F2937", border: "1px solid #374151", borderRadius: 10, padding: "10px 14px", marginBottom: 8 }}>
          <span style={{ fontWeight: "600", color: "#F9FAFB" }}>{ex.name}</span>
          <button onClick={() => removeExercise(i)} style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>
      ))}
      <button onClick={() => onSave({ name, location, exercises })} style={{ ...btnStyle("#059669"), marginTop: 16 }}>✓ Done</button>
    </div>
  );
}

// ─── WORKOUT SCREEN ───────────────────────────────────────────────────────────
export function WorkoutScreen({ userId, location, preloadedExercises, dayName, onBack }) {
  const [exercises, setExercises] = useState([]);
  const [exerciseName, setExerciseName] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [allExerciseNames, setAllExerciseNames] = useState([]);
  const [prMap, setPrMap] = useState({});

  useEffect(() => {
    const init = async () => {
      const snapshot = await getDocs(collection(db, "users", userId, "workouts"));
      const names = new Set();
      const prs = {};
      snapshot.forEach((d) => {
        d.data().exercises?.forEach((ex) => {
          names.add(ex.name);
          ex.sets?.forEach((s) => {
            const w = parseFloat(s.weight) || 0;
            if (!prs[ex.name] || w > prs[ex.name]) prs[ex.name] = w;
          });
        });
      });
      setAllExerciseNames([...names]);
      setPrMap(prs);
      if (preloadedExercises?.length > 0) {
        const loaded = await Promise.all(
          preloadedExercises.map(async (ex) => {
            const lastSets = await getLastSets(ex.name);
            return { name: ex.name, sets: lastSets || [{ reps: "", weight: "" }], notes: "" };
          })
        );
        setExercises(loaded);
      }
    };
    init();
  }, []);

  const getLastSets = async (name) => {
    try {
      const snapshot = await getDocs(query(collection(db, "users", userId, "workouts"), orderBy("date", "desc"), limit(20)));
      for (const d of snapshot.docs) {
        const match = d.data().exercises?.find((ex) => ex.name.toLowerCase() === name.toLowerCase());
        if (match?.sets?.length > 0) return match.sets;
      }
    } catch (e) {}
    return null;
  };

  const handleNameChange = (val) => {
    setExerciseName(val);
    if (val.trim().length < 1) { setSuggestions([]); return; }

    const input = val.toLowerCase();

    const dbMatches = EXERCISE_DB.filter(e =>
      e.name.toLowerCase().includes(input)
    );

    const matchedMuscles = new Set(dbMatches.flatMap(e => e.muscles));
    const related = EXERCISE_DB.filter(e =>
      !dbMatches.includes(e) &&
      e.muscles.some(m => matchedMuscles.has(m))
    );

    const dbNames = dbMatches.map(e => e.name);
    const historyMatches = allExerciseNames.filter(n =>
      n.toLowerCase().includes(input) && !dbNames.includes(n)
    );
    const relatedNames = related.map(e => e.name);

    const combined = [...new Set([...dbNames, ...historyMatches, ...relatedNames])];
    setSuggestions(combined.slice(0, 7));
  };

  const addExercise = async (name) => {
    const n = (name || exerciseName).trim();
    if (!n) return;
    if (exercises.find((ex) => ex.name.toLowerCase() === n.toLowerCase())) {
      setExerciseName(""); setSuggestions([]); return;
    }
    const lastSets = await getLastSets(n);
    setExercises([...exercises, { name: n, sets: lastSets || [{ reps: "", weight: "" }], notes: "" }]);
    setExerciseName(""); setSuggestions([]);
  };

  const addSet = (exIndex) => {
    const updated = [...exercises];
    const sets = updated[exIndex].sets;
    const last = sets[sets.length - 1];
    sets.push({ reps: last.reps, weight: last.weight });
    setExercises([...updated]);
  };

  const removeSet = (exIndex, setIndex) => {
    const updated = [...exercises];
    if (updated[exIndex].sets.length === 1) return;
    updated[exIndex].sets = updated[exIndex].sets.filter((_, i) => i !== setIndex);
    setExercises([...updated]);
  };

  const updateSet = (exIndex, setIndex, field, value) => {
    const updated = [...exercises];
    updated[exIndex].sets[setIndex][field] = value;
    setExercises([...updated]);
  };

  const removeExercise = (exIndex) => setExercises(exercises.filter((_, i) => i !== exIndex));
  const onDragEnd = (result) => {
  if (!result.destination) return;
  const reordered = [...exercises];
  const [moved] = reordered.splice(result.source.index, 1);
  reordered.splice(result.destination.index, 0, moved);
  setExercises(reordered);
};

  const saveWorkout = async () => {
    if (exercises.length === 0) return;
    try {
      await addDoc(collection(db, "users", userId, "workouts"), {
        location, dayName: dayName || "Custom", exercises, date: Timestamp.now()
      });
      alert("Workout saved!");
      onBack();
    } catch (e) { alert("Error saving: " + e.message); }
  };

  const isPR = (exName, weight, exIndex, setIndex) => {
    const w = parseFloat(weight);
    if (!w) return false;
    if (w <= (prMap[exName] || 0)) return false;
    const currentSets = exercises[exIndex]?.sets || [];
    for (let i = 0; i < setIndex; i++) {
      if ((parseFloat(currentSets[i]?.weight) || 0) >= w) return false;
    }
    return true;
  };

  return (
    <div className="fade-slide">
      <button onClick={onBack} style={backBtn}>← Back</button>
      <h2>💪 {dayName || location + " Workout"}</h2>
      <div style={{ position: "relative", marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={exerciseName}
            onChange={(e) => handleNameChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addExercise()}
            placeholder="Add exercise"
            style={inputStyle}
          />
          <button onClick={() => addExercise()} style={btnStyle("#4F46E5", true)}>Add</button>
        </div>
        {suggestions.length > 0 && (
          <div style={{ position: "absolute", top: "100%", left: 0, right: 48, background: "#1F2937", border: "1px solid #374151", borderRadius: 10, zIndex: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", overflow: "hidden" }}>
            {suggestions.map((s, i) => (
              <div
                key={i}
                onClick={() => addExercise(s)}
                style={{ padding: "11px 14px", cursor: "pointer", color: "#F9FAFB", fontSize: 14, fontWeight: "600", borderBottom: i < suggestions.length - 1 ? "1px solid #374151" : "none" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#374151"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >{s}</div>
            ))}
          </div>
        )}
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
  <Droppable droppableId="exercises">
    {(provided) => (
      <div {...provided.droppableProps} ref={provided.innerRef}>
        {exercises.map((ex, exIndex) => (
          <Draggable key={`ex-${exIndex}`} draggableId={`ex-${exIndex}`} index={exIndex}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                className="fade-slide"
                style={{
                  background: "#1F2937",
                  border: snapshot.isDragging ? "1px solid #4F46E5" : "1px solid #374151",
                  borderRadius: 14, padding: 16, marginBottom: 14,
                  boxShadow: snapshot.isDragging ? "0 8px 32px rgba(79,70,229,0.3)" : "none",
                  ...provided.draggableProps.style
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span {...provided.dragHandleProps} style={{ color: "#4B5563", fontSize: 20, cursor: "grab", userSelect: "none", touchAction: "none" }}>⠿</span>
                    <h3 style={{ margin: 0, color: "#F9FAFB", fontSize: 17, fontWeight: "700" }}>{ex.name}</h3>
                  </div>
                  <button onClick={() => removeExercise(exIndex)} style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: 18 }}>✕</button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontWeight: "700", color: "#6B7280", fontSize: 12, textTransform: "uppercase" }}>Reps</span>
                  <span style={{ fontWeight: "700", color: "#6B7280", fontSize: 12, textTransform: "uppercase" }}>Weight (kg)</span>
                  <span></span>
                </div>

                {ex.sets.map((set, setIndex) => {
                  const pr = isPR(ex.name, set.weight, exIndex, setIndex);
                  return (
                    <SwipeToDelete key={setIndex} onDelete={() => removeSet(exIndex, setIndex)} threshold={80}>
                      <div style={{ background: "#1F2937" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 4 }}>
                          <input type="number" value={set.reps} onChange={(e) => updateSet(exIndex, setIndex, "reps", e.target.value)} placeholder="0" style={inputStyle} />
                          <div style={{ position: "relative" }}>
                            <input type="number" value={set.weight} onChange={(e) => updateSet(exIndex, setIndex, "weight", e.target.value)} placeholder="0"
                              style={{ ...inputStyle, border: pr ? "1.5px solid #F59E0B" : inputStyle.border, paddingRight: pr ? 32 : undefined }} />
                            {pr && <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14 }}>🏆</span>}
                          </div>
                          <button onClick={() => removeSet(exIndex, setIndex)} style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: 16, lineHeight: "42px", padding: 0 }}>✕</button>
                        </div>
                        {pr && <div style={{ fontSize: 12, color: "#F59E0B", fontWeight: "700", marginBottom: 4 }}>🏆 New PR! Beat your previous {prMap[ex.name]}kg</div>}
                        <input type="text" value={set.notes || ""} onChange={(e) => updateSet(exIndex, setIndex, "notes", e.target.value)}
                          placeholder="Notes on this set..." style={{ ...inputStyle, marginBottom: 8, marginTop: 0, fontSize: 13 }} />
                      </div>
                    </SwipeToDelete>
                  );
                })}

                <ExerciseFooter exIndex={exIndex} addSet={addSet} />
                <input type="text" value={ex.notes || ""}
                  onChange={(e) => { const updated = [...exercises]; updated[exIndex].notes = e.target.value; setExercises(updated); }}
                  placeholder="Notes for this exercise..." style={{ ...inputStyle, marginTop: 10 }} />
              </div>
            )}
          </Draggable>
        ))}
        {provided.placeholder}
      </div>
    )}
  </Droppable>
</DragDropContext>
      ))}
      {exercises.length > 0 && (
        <button onClick={saveWorkout} style={{ ...btnStyle("#4F46E5"), marginTop: 8 }}>💾 Save Workout</button>
      )}
    </div>
  );
}
// ─── RUN SCREEN ───────────────────────────────────────────────────────────────
export function RunScreen({ userId, onBack }) {
  const [runType, setRunType] = useState(null);
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");
  const [speed, setSpeed] = useState("");
  const [incline, setIncline] = useState("");
  const [notes, setNotes] = useState("");

  const saveRun = async () => {
    if (!distance || !duration) { alert("Please fill in distance and duration"); return; }
    try {
      await addDoc(collection(db, "users", userId, "runs"), {
        type: runType, distance: parseFloat(distance), duration: parseInt(duration),
        speed: speed ? parseFloat(speed) : null,
        incline: incline ? parseFloat(incline) : null,
        notes, date: Timestamp.now(),
      });
      alert("Run saved!");
      onBack();
    } catch (e) { alert("Error saving: " + e.message); }
  };

  if (!runType) return (
    <div className="fade-slide">
      <button onClick={onBack} style={backBtn}>← Back</button>
      <h2>🏃 Log a Run</h2>
      <p style={{ color: "#9CA3AF", marginBottom: 20 }}>What type of run?</p>
      <button onClick={() => setRunType("Outdoor")} style={btnStyle("#059669")}>🌲 Outdoor Run</button>
      <button onClick={() => setRunType("Treadmill")} style={btnStyle("#0891B2")}>🏃 Treadmill</button>
    </div>
  );

  return (
    <div className="fade-slide">
      <button onClick={() => setRunType(null)} style={backBtn}>← Back</button>
      <h2>{runType === "Outdoor" ? "🌲 Outdoor Run" : "🏃 Treadmill"}</h2>
      <label style={labelStyle}>Distance (km)</label>
      <input type="number" value={distance} onChange={(e) => setDistance(e.target.value)} placeholder="5.0" style={{ ...inputStyle, marginBottom: 16 }} />
      <label style={labelStyle}>Duration (minutes)</label>
      <input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="30" style={{ ...inputStyle, marginBottom: 16 }} />
      {runType === "Treadmill" && (<>
        <label style={labelStyle}>Speed (km/h)</label>
        <input type="number" value={speed} onChange={(e) => setSpeed(e.target.value)} placeholder="8.5" style={{ ...inputStyle, marginBottom: 16 }} />
        <label style={labelStyle}>Incline (%)</label>
        <input type="number" value={incline} onChange={(e) => setIncline(e.target.value)} placeholder="2" style={{ ...inputStyle, marginBottom: 16 }} />
      </>)}
      <label style={labelStyle}>Notes (optional)</label>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="How did it feel?" style={{ ...inputStyle, marginBottom: 16, height: 80, resize: "none" }} />
      {distance && duration && (
        <div style={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
          <span style={{ color: "#059669", fontWeight: "700", fontSize: 15 }}>⚡ Avg pace: {(parseInt(duration) / parseFloat(distance)).toFixed(2)} min/km</span>
        </div>
      )}
      <button onClick={saveRun} style={btnStyle("#059669")}>💾 Save Run</button>
    </div>
  );
}

// ─── HISTORY SCREEN ───────────────────────────────────────────────────────────
export function HistoryScreen({ userId, onBack, onProgress }) {
  const [workouts, setWorkouts] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("workouts");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      const wSnap = await getDocs(query(collection(db, "users", userId, "workouts"), orderBy("date", "desc")));
      setWorkouts(wSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      const rSnap = await getDocs(query(collection(db, "users", userId, "runs"), orderBy("date", "desc")));
      setRuns(rSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    };
    fetchAll();
  }, []);

  const deleteWorkout = async (id) => {
    if (!window.confirm("Delete this workout?")) return;
    await deleteDoc(doc(db, "users", userId, "workouts", id));
    setWorkouts(prev => prev.filter((w) => w.id !== id));
  };

  const deleteRun = async (id) => {
    if (!window.confirm("Delete this run?")) return;
    await deleteDoc(doc(db, "users", userId, "runs", id));
    setRuns(prev => prev.filter((r) => r.id !== id));
  };

  const formatDate = (ts) => {
    if (!ts) return "";
    return ts.toDate().toLocaleDateString("no-NO", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  };

  const totalVolume = (w) => {
    let vol = 0;
    w.exercises?.forEach(ex => ex.sets?.forEach(s => {
      vol += ((parseFloat(s.reps) || 0) * (parseFloat(s.weight) || 0));
    }));
    return vol;
  };

  return (
    <div className="fade-slide">
      <button onClick={onBack} style={backBtn}>← Back</button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>📋 History</h2>
        <button onClick={onProgress} style={{ background: "linear-gradient(135deg, #4F46E5, #7C3AED)", color: "white", border: "none", borderRadius: 10, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: "700" }}>📈 Progress</button>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        <button onClick={() => setTab("workouts")} style={{ ...tabBtn, background: tab === "workouts" ? "#4F46E5" : "#1F2937", color: tab === "workouts" ? "white" : "#9CA3AF", border: "1px solid #374151", flex: 1 }}>💪 Workouts ({workouts.length})</button>
        <button onClick={() => setTab("runs")} style={{ ...tabBtn, background: tab === "runs" ? "#059669" : "#1F2937", color: tab === "runs" ? "white" : "#9CA3AF", border: "1px solid #374151", flex: 1 }}>🏃 Runs ({runs.length})</button>
      </div>
      {loading && <div style={{ textAlign: "center", padding: 40, color: "#6B7280" }}>Loading...</div>}
      {tab === "workouts" && !loading && (
        workouts.length === 0
          ? <div style={{ textAlign: "center", padding: 40, color: "#6B7280" }}><p style={{ fontSize: 32 }}>💪</p><p>No workouts logged yet.</p></div>
          : workouts.map((w, idx) => {
            const vol = totalVolume(w);
            const isOpen = expanded === w.id;
            return (
              <div key={w.id} className="fade-slide" style={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 14, marginBottom: 12, overflow: "hidden", animationDelay: `${idx * 0.04}s` }}>
                <div onClick={() => setExpanded(isOpen ? null : w.id)} style={{ padding: "14px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: "800", fontSize: 15, color: "#F9FAFB" }}>{w.dayName || w.location + " Workout"}</div>
                    <div style={{ fontSize: 13, color: "#6B7280", marginTop: 3 }}>
                      {formatDate(w.date)} · {w.exercises?.length || 0} exercises
                      {vol > 0 && <span style={{ color: "#F97316", marginLeft: 8, fontWeight: "700" }}>· {vol.toLocaleString()} kg total</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button onClick={(e) => { e.stopPropagation(); deleteWorkout(w.id); }} style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: 16, padding: 4 }}>🗑</button>
                    <span style={{ color: "#6B7280", fontSize: 18, display: "inline-block", transition: "transform 0.2s", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}>›</span>
                  </div>
                </div>
                {isOpen && (
                  <div style={{ borderTop: "1px solid #374151", padding: "12px 16px" }}>
                    {w.exercises?.map((ex, i) => (
                      <div key={i} style={{ marginBottom: 12 }}>
                        <div style={{ fontWeight: "700", color: "#F9FAFB", fontSize: 14, marginBottom: 6 }}>{ex.name}</div>
                        {ex.sets?.map((s, si) => (
                          <div key={si} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                            <span style={{ background: "#374151", color: "#9CA3AF", fontSize: 11, fontWeight: "700", padding: "2px 7px", borderRadius: 6 }}>SET {si + 1}</span>
                            <span style={{ color: "#F9FAFB", fontSize: 13 }}>{s.reps} reps</span>
                            <span style={{ color: "#6B7280", fontSize: 13 }}>@</span>
                            <span style={{ color: "#F97316", fontSize: 13, fontWeight: "700" }}>{s.weight} kg</span>
                            {s.notes && <span style={{ color: "#6B7280", fontSize: 12, fontStyle: "italic" }}>· {s.notes}</span>}
                          </div>
                        ))}
                        {ex.notes && <div style={{ fontSize: 12, color: "#6B7280", fontStyle: "italic", marginTop: 4 }}>📝 {ex.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
      )}
      {tab === "runs" && !loading && (
        runs.length === 0
          ? <div style={{ textAlign: "center", padding: 40, color: "#6B7280" }}><p style={{ fontSize: 32 }}>🏃</p><p>No runs logged yet.</p></div>
          : runs.map((r, idx) => (
            <div key={r.id} className="fade-slide" style={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 14, padding: 16, marginBottom: 12, animationDelay: `${idx * 0.04}s` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: "800", fontSize: 15, color: "#F9FAFB" }}>{r.type === "Outdoor" ? "🌲 Outdoor Run" : "🏃 Treadmill"}</div>
                  <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>{formatDate(r.date)}</div>
                </div>
                <button onClick={() => deleteRun(r.id)} style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: 16 }}>🗑</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                {[{ label: "Distance", value: `${r.distance} km`, color: "#059669" },
                  { label: "Duration", value: `${r.duration} min`, color: "#0891B2" },
                  { label: "Pace", value: `${(r.duration / r.distance).toFixed(2)} /km`, color: "#F97316" }
                ].map((stat) => (
                  <div key={stat.label} style={{ background: "#111827", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                    <div style={{ color: stat.color, fontWeight: "800", fontSize: 15 }}>{stat.value}</div>
                    <div style={{ color: "#6B7280", fontSize: 11, marginTop: 2 }}>{stat.label}</div>
                  </div>
                ))}
              </div>
              {r.notes && <p style={{ fontSize: 13, color: "#9CA3AF", marginTop: 10, marginBottom: 0, fontStyle: "italic" }}>{r.notes}</p>}
            </div>
          ))
      )}
    </div>
  );
}

// ─── PROGRESS SCREEN ─────────────────────────────────────────────────────────
export function ProgressScreen({ userId, onBack }) {
  const [workouts, setWorkouts] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState("");
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const snap = await getDocs(query(collection(db, "users", userId, "workouts"), orderBy("date", "asc")));
      const data = snap.docs.map(d => ({ ...d.data(), id: d.id }));
      setWorkouts(data);
      const names = new Set();
      data.forEach(w => w.exercises?.forEach(ex => names.add(ex.name)));
      const sorted = [...names].sort();
      setAllExercises(sorted);
      if (sorted.length > 0) buildChart(sorted[0], data);
      setLoading(false);
    };
    fetchData();
  }, []);

  const buildChart = (name, data) => {
    setSelectedExercise(name);
    const points = [];
    (data || workouts).forEach(w => {
      const ex = w.exercises?.find(e => e.name.toLowerCase() === name.toLowerCase());
      if (!ex || !ex.sets?.length) return;
      const maxWeight = Math.max(...ex.sets.map(s => parseFloat(s.weight) || 0));
      const totalVol = ex.sets.reduce((sum, s) => sum + ((parseFloat(s.reps) || 0) * (parseFloat(s.weight) || 0)), 0);
      const date = w.date?.toDate().toLocaleDateString("no-NO", { day: "numeric", month: "short" });
      if (maxWeight > 0) points.push({ date, maxWeight, volume: Math.round(totalVol) });
    });
    setChartData(points);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 10, padding: "10px 14px" }}>
        <p style={{ color: "#9CA3AF", fontSize: 12, margin: "0 0 6px 0" }}>{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color, fontWeight: "700", fontSize: 14, margin: "2px 0" }}>
            {p.name}: {p.value} kg
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="fade-slide">
      <button onClick={onBack} style={backBtn}>← Back</button>
      <h2>📈 Progress</h2>
      {loading && <div style={{ textAlign: "center", padding: 40, color: "#6B7280" }}>Loading...</div>}
      {!loading && allExercises.length === 0 && (
        <div style={{ textAlign: "center", padding: 40, color: "#6B7280" }}>
          <p style={{ fontSize: 32 }}>📊</p>
          <p>No workout data yet. Log some workouts to see your progress!</p>
        </div>
      )}
      {!loading && allExercises.length > 0 && (
        <>
          <label style={labelStyle}>Select Exercise</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 }}>
            {allExercises.map((name) => (
              <button key={name} onClick={() => buildChart(name, null)}
                style={{ background: selectedExercise === name ? "#4F46E5" : "#1F2937", color: selectedExercise === name ? "white" : "#9CA3AF", border: "1px solid #374151", borderRadius: 20, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: "600" }}>
                {name}
              </button>
            ))}
          </div>
          {chartData.length < 2 ? (
            <div style={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 12, padding: 20, textAlign: "center", color: "#6B7280", marginBottom: 20 }}>
              Log at least 2 sessions of <strong style={{ color: "#F9FAFB" }}>{selectedExercise}</strong> to see a chart.
            </div>
          ) : (
            <>
              <div style={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <p style={{ color: "#9CA3AF", fontSize: 12, fontWeight: "700", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 12 }}>Max Weight Over Time</p>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#6B7280", fontSize: 11 }} unit=" kg" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="maxWeight" name="Max Weight" stroke="#F97316" strokeWidth={2.5} dot={{ fill: "#F97316", r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div style={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <p style={{ color: "#9CA3AF", fontSize: 12, fontWeight: "700", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 12 }}>Volume Per Session (reps × kg)</p>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#6B7280", fontSize: 11 }} unit=" kg" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="volume" name="Volume" stroke="#4F46E5" strokeWidth={2.5} dot={{ fill: "#4F46E5", r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div style={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 12, padding: 14, textAlign: "center" }}>
                  <div style={{ color: "#F97316", fontWeight: "800", fontSize: 22 }}>{Math.max(...chartData.map(d => d.maxWeight))} kg</div>
                  <div style={{ color: "#6B7280", fontSize: 12, marginTop: 4 }}>Best Weight</div>
                </div>
                <div style={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 12, padding: 14, textAlign: "center" }}>
                  <div style={{ color: "#4F46E5", fontWeight: "800", fontSize: 22 }}>{chartData.length}</div>
                  <div style={{ color: "#6B7280", fontSize: 12, marginTop: 4 }}>Sessions Logged</div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

